import React, { useState } from "react";
import { Box, Container, Typography, Stack, Avatar, IconButton } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const MONO   = "'JetBrains Mono', 'Fira Code', ui-monospace, monospace";
const ACCENT = "#E2684A";
const PURPLE = "#7C3AED";
const BG     = "#030014";
const BG2    = "#0F0728";
const LINE   = "#1E2130";
const INK    = "#E7E9EE";
const INK2   = "#B6BBC6";
const INK3   = "#7D8390";
const R      = "14px";

const testimonials = [
  { id: 1, quote: "Beanbag AI transformed our customer support operations. Their voice agents handle 70% of our inquiries with near-human quality, and we deployed in just 3 weeks.", author: "Sarah Chen",     role: "VP of Operations",  company: "TechFlow Inc." },
  { id: 2, quote: "The custom LLM they built for our legal document analysis cut our review time by 80%. The ROI was visible within the first month.",                                author: "Michael Torres", role: "Chief Legal Officer", company: "FinanceHub" },
  { id: 3, quote: "From strategy to deployment, the Beanbag team delivered an end-to-end AI solution that actually works in production. No failed POCs here.",                       author: "Priya Sharma",   role: "CTO",                company: "HealthPlus Systems" },
];

const Testimonials = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const t = testimonials[activeIndex];

  return (
    <Box sx={{ py: { xs: 10, md: 13 }, borderBottom: `1px solid ${LINE}`, background: BG }}>
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
        >
          <Typography sx={{ fontFamily: MONO, fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase", color: ACCENT, mb: 1.75 }}>
            Client Success Stories
          </Typography>
          <Typography sx={{
            fontFamily: "'IBM Plex Serif', serif",
            fontSize: { xs: "1.75rem", md: "2.5rem" },
            fontWeight: 600, letterSpacing: "-0.015em", lineHeight: 1.15,
            color: INK, mb: 5,
          }}>
            What Our Partners Say
          </Typography>
        </motion.div>

        <Box sx={{
          p: { xs: 3.5, md: 5 }, borderRadius: R,
          background: BG2, border: `1px solid ${LINE}`,
          position: "relative",
        }}>
          {/* Large quote mark */}
          <Typography sx={{
            position: "absolute", top: 16, left: 28,
            fontFamily: "'IBM Plex Serif', serif",
            fontSize: "80px", lineHeight: 1, color: `${ACCENT}20`,
            userSelect: "none", pointerEvents: "none",
          }}>
            "
          </Typography>

          <AnimatePresence mode="wait">
            <motion.div
              key={t.id}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.35 }}
            >
              <Typography sx={{
                color: INK, fontStyle: "italic", fontSize: { xs: "1.05rem", md: "1.2rem" },
                lineHeight: 1.75, mb: 4, mt: 3, maxWidth: 700,
              }}>
                "{t.quote}"
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar sx={{
                  width: 48, height: 48,
                  background: `linear-gradient(135deg, ${ACCENT}, ${PURPLE})`,
                  fontWeight: 700, fontSize: "15px",
                }}>
                  {t.author.split(" ").map(n => n[0]).join("")}
                </Avatar>
                <Box>
                  <Typography sx={{ color: INK, fontWeight: 600, fontSize: "15px" }}>{t.author}</Typography>
                  <Typography sx={{ color: INK3, fontSize: "13px", fontFamily: MONO }}>{t.role}, {t.company}</Typography>
                </Box>
              </Stack>
            </motion.div>
          </AnimatePresence>

          {/* Nav arrows */}
          <Stack direction="row" spacing={1} sx={{ position: "absolute", bottom: 24, right: 24 }}>
            <IconButton
              size="small"
              onClick={() => setActiveIndex(p => p === 0 ? testimonials.length - 1 : p - 1)}
              sx={{ border: `1px solid ${LINE}`, color: INK2, "&:hover": { borderColor: ACCENT, color: ACCENT, background: `${ACCENT}10` } }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setActiveIndex(p => p === testimonials.length - 1 ? 0 : p + 1)}
              sx={{ border: `1px solid ${LINE}`, color: INK2, "&:hover": { borderColor: ACCENT, color: ACCENT, background: `${ACCENT}10` } }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {/* Dot indicators */}
        <Stack direction="row" spacing={1} justifyContent="center" mt={3}>
          {testimonials.map((_, i) => (
            <Box key={i} onClick={() => setActiveIndex(i)} sx={{
              width: i === activeIndex ? 24 : 8, height: 8, borderRadius: 4,
              background: i === activeIndex ? ACCENT : LINE,
              cursor: "pointer", transition: "all 0.3s",
            }} />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Testimonials;
